import React from 'react';
import { Bot } from 'lucide-react';
import { characterFor } from './characters';

// Small Stage building blocks, matching auto-appy src/features/Workspace/WorkspaceUi.jsx.

export function AgentAvatar({ characterKey, size = 36, name = '', icon: Icon = Bot }) {
  const character = characterFor(characterKey);
  const radius = Math.round(size * 0.3);
  if (!character) {
    return (
      <span className="ws-avatar ws-avatar--blank" style={{ width: size, height: size, borderRadius: radius }} aria-hidden="true">
        <Icon size={Math.max(14, Math.round(size * 0.44))} strokeWidth={1.9} />
      </span>
    );
  }
  return (
    <span className="ws-avatar" style={{ width: size, height: size, borderRadius: radius, background: character.tint }}>
      <img src={character.image} alt={name ? `${name}'s character` : ''} loading="lazy" />
    </span>
  );
}

export function Monogram({ name = '', size = 32 }) {
  const initials = name.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'AA';
  return <span className="ws-monogram" style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }} aria-hidden="true">{initials}</span>;
}

const STATUS_LABELS = { live: 'Live', paused: 'Paused', draft: 'Draft', failed: 'Failed', warning: 'Warning', info: 'Scheduled' };

export function Status({ tone = 'draft', children }) {
  return <span className={`ws-status ws-status--${tone}`}>{children || STATUS_LABELS[tone] || tone}</span>;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, style }) {
  return <span className="ws-skeleton" style={{ display: 'block', width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

// A character on its tinted stage. Full-body art fits inside the frame, so heads
// and feet are never clipped; head-only art sits centred.
export function CharacterStage({ character, height = 188, children, empty = null }) {
  return (
    <div className="ws-stage" style={{ height, background: character?.tint || undefined }}>
      {character && <span className="ws-stage-glow" aria-hidden="true" />}
      {character && character.full && <img className="ws-stage-full" src={character.full} alt="" />}
      {character && !character.full && <img className="ws-stage-face" src={character.image} alt="" />}
      {!character && empty}
      {children}
    </div>
  );
}

// Page header in the main app's style: eyebrow, large title, one line of help, actions.
export function StageHeader({ eyebrow, title, description, children, below }) {
  return (
    <header className="ws-hero ai-hero">
      <div className="ws-grow">
        {eyebrow && <div className="ws-eyebrow">{eyebrow}</div>}
        <h1 className="ws-title" style={{ marginTop: eyebrow ? 6 : 0 }}>{title}</h1>
        {description && <p className="ws-muted ai-hero-copy">{description}</p>}
        {below}
      </div>
      {children && <div className="ai-hero-actions">{children}</div>}
    </header>
  );
}

export function StagePage({ children, className = '', wide = false, label }) {
  return (
    <main className={`ws ai-stage ${className}`.trim()} aria-label={label}>
      <div className="ws-page" style={wide ? { maxWidth: 1440 } : undefined}>{children}</div>
    </main>
  );
}

export const firstName = (name = '') => String(name).trim().split(/\s+/)[0] || '';

export function greetingFor(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export const formatNumber = (value) => (value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-IN') : '—');

export function formatDay(value, now = new Date()) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((startOf(date) - startOf(now)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

export function formatTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toUpperCase()}`);
}

// "Today, 4:05 PM" / "3 Sept" — short enough for a table cell.
export function formatWhen(value, now = new Date()) {
  const day = formatDay(value, now);
  if (!day) return '—';
  return ['Today', 'Yesterday'].includes(day) ? `${day}, ${formatTime(value)}` : day;
}
